import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { MinusIcon, PlusIcon } from '@phosphor-icons/react';
import {
  MOBILITY_PLANNER_MAX_PASSENGERS,
  type MobilityPlannerTripType,
} from '@voll-intelligence/genui-contract';
import { segmentedRecipe } from '@voll-intelligence/ui-kit/ui/segmented-control';
import { cn } from '@voll-intelligence/ui-kit/utils';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { LABEL } from './planner_parts.js';

const FOCUS = 'outline-none focus-visible:ring-3 focus-visible:ring-ring/50';

const TRIP_TYPES = ['one_way', 'round_trip'] as const satisfies readonly MobilityPlannerTripType[];

/** A receita do `SegmentedControl` (sm): o segmento ligado veste o thumb. No celular a
 *  trilha ocupa a largura. */
const TRACK = cn(
  segmentedRecipe.track('sm'),
  'inline-flex @max-[theme(--breakpoint-sm)]:flex @max-[theme(--breakpoint-sm)]:w-full',
);
const SEGMENT = cn(segmentedRecipe.segment('sm'), 'flex-1', FOCUS);

const STEP: Partial<Record<string, number>> = { ArrowRight: 1, ArrowLeft: -1 };

/**
 * O Sentido (Só ida / Ida e volta) com a cara do `VOLL/SegmentedControl` e a
 * semântica de `radiogroup` (o `SegmentedControl` do DS é um `ToggleGroup`, que
 * anuncia botões pressionados): um só ponto de Tab, e ←/→ trocam o sentido.
 */
export function TripTypeControl({
  value,
  onChange,
}: Readonly<{
  value: MobilityPlannerTripType;
  onChange: (value: MobilityPlannerTripType) => void;
}>): ReactNode {
  const { t } = useGenUiI18n();
  const labelId = useId();
  const group = useRef<HTMLDivElement>(null);
  const labels = {
    one_way: t('mobilityPlanner.tripType.oneWay'),
    round_trip: t('mobilityPlanner.tripType.roundTrip'),
  } as const;
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = STEP[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const nextIndex = (index + step + TRIP_TYPES.length) % TRIP_TYPES.length;
    const next = TRIP_TYPES[nextIndex];
    if (!next) return;
    onChange(next);
    group.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[nextIndex]?.focus();
  };
  return (
    <div className="grid min-w-0 gap-1 @max-[theme(--breakpoint-sm)]:flex-1">
      <span id={labelId} className={LABEL}>
        {t('mobilityPlanner.tripType')}
      </span>
      <div ref={group} role="radiogroup" aria-labelledby={labelId} className={TRACK}>
        {TRIP_TYPES.map((type, index) => (
          <button
            key={type}
            type="button"
            role="radio"
            aria-checked={type === value}
            tabIndex={type === value ? 0 : -1}
            className={cn(SEGMENT, type === value && segmentedRecipe.active)}
            onClick={() => {
              onChange(type);
            }}
            onKeyDown={(event) => {
              onKeyDown(event, index);
            }}
          >
            {labels[type]}
          </button>
        ))}
      </div>
    </div>
  );
}

const STEPPER_BUTTON = `flex size-7 cursor-pointer items-center justify-center rounded-full text-foreground hover:bg-muted disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent ${FOCUS}`;

/** As Pessoas: o stepper de 1 a 4 (quem viaja e até 3 acompanhantes). O valor
 *  é anunciado a cada troca (`aria-live`); o «−» desliga em 1 e o «+» no teto. */
export function PassengersStepper({
  value,
  onChange,
}: Readonly<{ value: number; onChange: (value: number) => void }>): ReactNode {
  const { t } = useGenUiI18n();
  const labelId = useId();
  return (
    <div className="grid shrink-0 gap-1">
      <span id={labelId} className={LABEL}>
        {t('mobilityPlanner.passengers')}
      </span>
      <div
        role="group"
        aria-labelledby={labelId}
        className="flex h-8 items-center gap-1 rounded-full border border-border bg-background px-0.5"
      >
        <button
          type="button"
          aria-label={t('mobilityPlanner.passengersLess')}
          disabled={value <= 1}
          className={STEPPER_BUTTON}
          onClick={() => {
            onChange(value - 1);
          }}
        >
          <MinusIcon className="size-3.5" aria-hidden="true" />
        </button>
        <span
          aria-live="polite"
          className="min-w-6 text-center text-ui text-foreground tabular-nums"
        >
          {value}
        </span>
        <button
          type="button"
          aria-label={t('mobilityPlanner.passengersMore')}
          disabled={value >= MOBILITY_PLANNER_MAX_PASSENGERS}
          className={STEPPER_BUTTON}
          onClick={() => {
            onChange(value + 1);
          }}
        >
          <PlusIcon className="size-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
